<template>
  <div>
    <transition appear name="loading-bar">
      <loading-bar v-show="pending"/>
    </transition>

    <b-card-body>
      <b-form class="d-flex mb-3 mx-n3 justify-between">
        <!--                <search-input v-model="search" class="mb-3 mx-3" />-->
        <b-form-group
            :label="$t('Search')"
            class="mx-3"
            label-for="filter-input"
        >
          <b-input-group>
            <b-form-input
                id="filter-input"
                v-model="filter"
                :placeholder="$t('Type to search')"
                type="search"
            />

            <b-input-group-append>
              <b-button :disabled="!filter" @click="filter = ''">
                Clear
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <b-form-group
            :label="$t('Tags')"
            class="mx-3"
            label-for="tags-input"
        >
          <tags-select
              id="tags-input"
              v-model="params.tags"
          />
        </b-form-group>

        <b-button v-b-modal.uploadAttachmentModal class="ml-auto">
          <fa fixed-width icon="file-upload"/>
          {{ $t('Upload attachment') }}
        </b-button>
      </b-form>

      <b-table
          id="projectAttachmentsTable"
          ref="table"
          :api-url="`/api/projects/${projectId}/attachments`"
          :busy.sync="pending"
          :current-page="currentPage"
          :fields="fields"
          :filter="filter"
          :items="fetchData"
          :per-page="perPage"
          @filtered="onFiltered"
      >
        <template #cell(name)="{item}">
          <div>{{ item.name }}</div>
          <b-badge
              v-for="tag in item.tags"
              :key="tag.id"
              class="m-1"
              pill
          >
            {{ tag.name }}
          </b-badge>
        </template>

        <template #cell(actions)="{item}">
          <b-button-group>
            <b-button
                v-b-tooltip
                :href="item.url"
                :title="$t('Download')"
                size="sm"
                target="_blank"
                variant="primary"
            >
              <fa class="mx-1" icon="download"/>
            </b-button>

            <upload-attachment
                :id="item.id"
                :project-id="projectId"
            >
              <template #activator="{toggle}">
                <b-button
                    v-b-tooltip
                    :title="$t('Edit')"
                    size="sm"
                    squared
                    @click="toggle"
                >
                  <fa class="mx-1" icon="pen"/>
                </b-button>
              </template>
            </upload-attachment>

            <b-button
                v-b-tooltip
                :disabled="item.project[0].pivot.deleted_at !== null"
                :title="$t('Delete')"
                size="sm"
                variant="danger"
                @click="handleDelete(`/api/projects/${projectId}/attachments/${item.id}`)"
            >
              <fa class="mx-1" icon="trash"/>
            </b-button>
          </b-button-group>
        </template>

        <template v-if="$auth.check('admin')" #cell(adminActions)="{item}">
          <b-button-group>
            <b-button
                v-b-tooltip
                :disabled="item.project[0].pivot.deleted_at === null"
                :title="$t('Restore')"
                size="sm"
                variant="success"
                @click="handleRestore(`/api/projects/${projectId}/attachments/${item.id}/restore`)"
            >
              <fa class="mx-1" icon="recycle"/>
            </b-button>

            <b-button
                v-b-tooltip
                :disabled="item.project[0].pivot.deleted_at === null"
                :title="$t('Force delete')"
                size="sm"
                variant="danger"
                @click="handleDelete(`/api/projects/${projectId}/attachments/${item.id}?force=true`)"
            >
              <fa class="mx-1" icon="times-circle"/>
            </b-button>
          </b-button-group>
        </template>
      </b-table>
    </b-card-body>

    <b-card-footer v-if="showPagination">
      <b-pagination
          v-model="currentPage"
          :per-page="perPage"
          :total-rows="totalRows"
          align="center"
          class="m-0"
      />
    </b-card-footer>
  </div>
</template>

<script>

import fetchTableData from "@/mixins/fetchTableData";
import TagsSelect from "@/components/selects/TagsSelect";
import UploadAttachment from "@/components/projects/UploadAttachment";
import crudListener from "@/mixins/crudListener";

export default {
  name: 'ProjectAttachmentsTab',
  components: {UploadAttachment, TagsSelect},
  mixins: [fetchTableData, crudListener],
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      filter: null,
      filterOn: [],
      defaultFields: [
        {
          key: 'name',
          label: this.$t('Name')
        },
        {
          key: 'description',
          label: this.$t('Description')
        },
        {
          key: 'user.name',
          label: this.$t('Created by'),
        },
        {
          key: 'created_at',
          label: this.$t('Created at'),
          class: 'text-nowrap',
          formatter: value => {
            return this.$moment(value).format('lll')
          }
        },
        {
          key: 'actions',
          label: this.$t('Actions')
        },
      ],
      params: {
        tags: []
      }
    }
  },
  computed: {
    resourceName() {
      return `projects/${this.projectId}/attachments`;
    },
    fields() {
      if (this.$auth.check('admin')) {
        const returnFields = this.defaultFields
        returnFields.push(
            {
              key: 'adminActions',
              label: this.$t('Admin Actions')
            }
        );
        return returnFields
      }
      return this.defaultFields
    },
  },
  methods: {
    handleRestore(apiUrl) {

      this.$bvModal.msgBoxConfirm(this.$t('Are you sure to restore this item?'), {
        title: this.$t('Are you sure?'),
        okVariant: 'success',
        okTitle: this.$t('Yes'),
        cancelTitle: this.$t('No'),
        hideHeaderClose: false,
        centered: true
      }).then(value => {
        if (!value) {
          return;
        }

        this.axios
            .post(apiUrl)
            .catch(e => e.response || throw e)
            .then(this.refresh)
      })

    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },

    refresh() {
      if (this.$refs.table) {
        this.$refs.table.refresh();
      }
    },

    handleCreated() {
      this.refresh();
    },

    handleUpdates() {
      this.refresh();
    }
  }
};
</script>
